import { ShiftMapper } from "@utils/shift-util.ts";
import { DateUtils } from "@/utils/dayUtill.ts";
import {
  TooltipProvider,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@components/ui/tooltip.tsx";
import type * as type from "@vTypes";
import useRosterConfig from "@/hooks/useRosterConfig";
import { cn } from "@/lib/utils";
import { useMemo } from "react";

interface ShiftTypeSelectorProps {
  shiftMapper: ShiftMapper | null;
  selectedMainShifts: string[];
  onMainShiftToggle: (shift: string) => void;
  selectedSubShifts: string[];
  onSubShiftToggle: (shift: string) => void;
  availableShifts: type.availableShifts[] | undefined;
  weekendOff: number | null;
  fixedShift: string | null;
  disabled?: boolean;
}

export default function ShiftTypeSelector({
  shiftMapper,
  selectedMainShifts,
  onMainShiftToggle,
  selectedSubShifts,
  onSubShiftToggle,
  availableShifts,
  weekendOff,
  fixedShift,
  disabled,
}: ShiftTypeSelectorProps) {
  const { data: serverConfig } = useRosterConfig();
  const isUseMid = serverConfig?.use_mid;

  const defaultShiftMetaMap = useMemo(() => {
    const map = new Map<
      string,
      {
        shift_id: string;
        color?: string;
        shift_gb?: string | null;
        name?: string;
      }
    >();
    (availableShifts || []).forEach((shift) => {
      if (shift.default_shift && !map.has(shift.default_shift)) {
        map.set(shift.default_shift, {
          shift_id: shift.shift_id,
          color: shift.color,
          shift_gb: shift.shift_gb,
          name: shift.name,
        });
      }
    });
    return map;
  }, [availableShifts]);

  const mainShifts = useMemo(
    () =>
      ["D", "E", "N", "M"]
        .filter((type) => isUseMid !== false || type !== "M")
        .map((type) => {
          const meta = defaultShiftMetaMap.get(type);
          return {
            type,
            label: meta?.shift_id || type,
            color: meta?.color,
            shiftGb: meta?.shift_gb ?? null,
          };
        }),
    [defaultShiftMetaMap, isUseMid]
  );

  // 주말 휴무 적용(1) + 근무형태 고정 선택 시 비활성화
  const isGlobalDisabled =
    (weekendOff === 1 && fixedShift !== null && fixedShift !== "") ||
    (disabled ?? false);

  const handleMainShiftClick = (type: string) => {
    const isSelected = selectedMainShifts.includes(type);

    // 메인 시프트 토글
    onMainShiftToggle(type);

    // 선택 해제 시, 해당 메인 시프트에 속한 서브 시프트들도 해제
    if (isSelected) {
      const label = mainShifts.find((m) => m.type === type)?.label;

      const subShiftsToRemove = (availableShifts || []).filter((sub) => {
        const shiftGbMatch = sub.shift_gb === label;
        return shiftGbMatch;
      });

      subShiftsToRemove.forEach((sub) => {
        if (selectedSubShifts.includes(sub.shift_id)) {
          onSubShiftToggle(sub.shift_id);
        }
      });
    }
  };

  return (
    <div className="mb-5">
      {/* 3개 컬럼 그리드 */}
      <div className={`grid ${isUseMid ? "grid-cols-4" : "grid-cols-3"} gap-1`}>
        {mainShifts.map(({ type, label, color, shiftGb }) => {
          const buttonColor = color || shiftMapper?.getColor(type);
          // 빈 배열 = 모든 근무형태 활성화 (모두 선택된 상태)
          // 배열에 값이 있으면 = 해당 값들만 포함 (선택된 상태)
          const isSelected =
            selectedMainShifts.length === 0 ||
            selectedMainShifts.includes(type);
          const isMainBtnDisabled = isGlobalDisabled;
          // const isSubBtnDisabled = isGlobalDisabled || !isSelected;

          // 해당 메인 근무에 속하는 서브 근무 필터링
          const subShifts = (availableShifts || []).filter((sub) => {
            // 메인 시프트 자체는 제외
            if (sub.default_shift === type) return false;
            if (sub.shift_id === label) return false;
            // 메인 시프트와 같은 그룹(shift_gb)만 서브로 표시
            if (!shiftGb) return false;
            return sub.shift_gb === shiftGb;
          });

          // 현재 그룹 내에서 선택된 서브 근무가 있는지 확인
          const selectedSubInGroup = subShifts.find((s) =>
            selectedSubShifts.includes(s.shift_id)
          );

          return (
            <div key={type} className="flex flex-col">
              {/* 메인 근무 헤더 (버튼) */}
              <button
                type="button"
                onClick={() => !isMainBtnDisabled && handleMainShiftClick(type)}
                disabled={isMainBtnDisabled}
                className={`mb-1! w-full px-4 py-1.5 rounded-[5px] text-center font-bold border border-[#D6D6DE] transition-colors ${
                  isSelected ? "text-white" : "bg-white text-[#939399]"
                } ${isMainBtnDisabled ? "cursor-not-allowed" : ""}`}
                style={isSelected ? { backgroundColor: buttonColor } : {}}
              >
                <span className="text-sm font-pretendard">{label}</span>
              </button>

              {/* 서브 근무 버튼들 - 가로로 나열, 3개 넘으면 줄바꿈 */}
              <div
                className={cn(
                  "grid gap-1",
                  isUseMid ? "grid-cols-3" : "grid-cols-3"
                )}
              >
                {subShifts.map((shift) => {
                  const isSubSelected = selectedSubShifts.includes(
                    shift.shift_id
                  );
                  const isSubBtnDisabled =
                    isGlobalDisabled ||
                    !isSelected ||
                    (!!selectedSubInGroup && !isSubSelected);

                  return (
                    <TooltipProvider>
                      <Tooltip key={shift.shift_id}>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={() =>
                              !isSubBtnDisabled &&
                              onSubShiftToggle(shift.shift_id)
                            }
                            disabled={isSubBtnDisabled}
                            className={`flex justify-center items-center border border-[#D6D6DE] p-1 rounded-[5px]  aspect-square  transition-colors ${
                              isSubSelected
                                ? `text-white font-bold`
                                : "bg-white text-[#939399]"
                            } ${isSubBtnDisabled ? "cursor-not-allowed" : ""}`}
                            style={
                              isSubSelected
                                ? { backgroundColor: shift.color }
                                : {}
                            }
                          >
                            <span
                              className={`text-xs font-pretendard font-medium ${
                                isSubSelected ? "text-white" : "text-[#939399]"
                              }`}
                            >
                              {shift.shift_id}
                            </span>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent
                          className="bg-[#333333] opacity-80"
                          arrowClassName="fill-[#333333]"
                        >
                          <p>{`${DateUtils.formatTimeString(shift.start_time)} ~ ${DateUtils.formatTimeString(shift.end_time)}`}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
